/**
 * Unlocking the vault with a passkey.
 *
 * A third peer wrap of the master key, beside the password-derived KEK and the
 * 24-word recovery phrase:
 *
 *   password  ──Argon2id(kek_salt)─────→ KEK ─┐
 *   24 words  ──HKDF(entropy)────────────→ RK ─┼──→ masterKey
 *   passkey   ──WebAuthn PRF ──HKDF──────→ PK ─┘
 *
 * Any one of the three opens it; none reveals the others; removing one leaves
 * the rest working. That is the same contract the first two already had, and
 * the reason this is a wrap rather than a login.
 *
 * ── Why there is no server ceremony ─────────────────────────────────────────
 * This is **not an authentication factor**. The session was already
 * authenticated by mauvely.com. What the authenticator contributes is the PRF
 * output: 32 bytes it derives from its own non-extractable secret and the salt
 * we hand it, identical every time, and never transmitted anywhere. HKDF turns
 * that into an AES key which wraps the master key. The server stores the
 * ciphertext and a salt, verifies nothing, and could not do anything useful
 * with a forged credential id even if it accepted one.
 *
 * ── The extractable-master problem ──────────────────────────────────────────
 * Enrolling needs the master key's *bytes*, and the everyday unlock deliberately
 * imports it non-extractable so an XSS cannot lift it. Rather than weaken that,
 * enrolment goes through `unlockForRewrap()` — the same path "change password"
 * already uses, which re-derives an extractable copy from the user's own secret
 * and keeps it alive only for the length of the operation.
 *
 * ── PRF support is patchy, and that is handled by hiding the feature ────────
 * Not every browser or authenticator implements the `prf` extension. Everything
 * here feature-detects and returns null rather than throwing, so an unsupported
 * browser simply never sees the option and falls back to the password it was
 * already using.
 */

import {
  aesDecrypt,
  aesEncrypt,
  fromBase64,
  hkdfAesKey,
  importAesKey,
  randomBytes,
  toBase64,
  toBase64Url,
  fromBase64Url,
  type Bytes
} from './primitives';
import { unlockForRewrap, WRAP_AAD_MASTER, type VaultBlobs } from './keys';

/** Domain separator for the PRF output. Part of the format: change it and every
 *  enrolled passkey stops opening its vault. */
const PASSKEY_INFO = 'mauvely.cloud.passkey.v1';

export const PRF_SALT_BYTES = 32;

/** The relying party is the origin the vault is served from. Cloud and the main
 *  site are different hosts, and a passkey enrolled here should not be offered
 *  there — it opens a vault, it does not sign anyone in. */
const rpId = (): string => window.location.hostname;

export interface StoredPasskey {
  id: string;
  credentialId: string;
  prfSalt: string;
  masterKeyWrapped: string;
  label: string | null;
  createdAt: number;
  lastUsedAt: number | null;
}

/**
 * Can this browser do PRF at all?
 *
 * Deliberately cheap and synchronous-ish: it checks the API surface exists, not
 * that a particular authenticator will cooperate. A device that turns out not
 * to support PRF is caught at enrolment, where `prfOutput` comes back null and
 * the caller gets a clear message instead of a broken passkey.
 */
export async function prfSupported(): Promise<boolean> {
  if (typeof PublicKeyCredential === 'undefined') return false;
  if (!window.isSecureContext) return false;

  try {
    // Newer browsers can answer directly. Where they cannot, fall back to
    // "a platform authenticator exists", which is necessary but not sufficient
    // — enrolment does the real check.
    const caps = (
      PublicKeyCredential as unknown as {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
      }
    ).getClientCapabilities;

    if (caps) {
      const result = await caps.call(PublicKeyCredential);
      if (typeof result?.extensionPrf === 'boolean') return result.extensionPrf;
    }

    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Pull the PRF result out of a credential, whichever shape it arrived in. */
function prfOutput(credential: PublicKeyCredential | null): Bytes | null {
  if (!credential) return null;
  const results = credential.getClientExtensionResults() as {
    prf?: { results?: { first?: ArrayBuffer } };
  };
  const first = results?.prf?.results?.first;
  return first ? (new Uint8Array(first) as Bytes) : null;
}

/** HKDF the PRF output into the key that wraps the master key. */
const wrappingKeyFrom = (prf: Bytes): Promise<CryptoKey> => hkdfAesKey(prf, PASSKEY_INFO);

export interface EnrolledPasskey {
  credentialId: string;
  prfSalt: string;
  masterKeyWrapped: string;
}

/**
 * Create a passkey and wrap the master key to it.
 *
 * `secret` is the user's password or recovery phrase — enrolment genuinely
 * needs it, because it needs the master key's bytes and only `unlockForRewrap`
 * produces them. Asking for it also means enrolling a passkey cannot be done by
 * an attacker who merely finds an unlocked tab.
 *
 * Some platforms return the PRF output from `create()` and some do not — the
 * spec allows both — so this creates the credential and then immediately asks
 * for an assertion when the first call came back empty. Two prompts on those
 * platforms, one on the rest, and a working passkey either way.
 */
export async function enrolPasskey(
  secret: { password: string } | { entropy: Bytes },
  blobs: VaultBlobs,
  user: { id: string; email: string; name: string }
): Promise<EnrolledPasskey | null> {
  const salt = randomBytes(PRF_SALT_BYTES);

  const created = (await navigator.credentials.create({
    publicKey: {
      // Unused: nothing verifies this, because nothing here is a ceremony. It
      // is required by the API and must be fresh, so it is random.
      challenge: randomBytes(32),
      rp: { id: rpId(), name: 'Mauvely Cloud' },
      user: {
        id: new TextEncoder().encode(user.id),
        name: user.email,
        displayName: user.name || user.email
      },
      // ES256 then RS256, the pair every authenticator supports. The algorithm
      // is irrelevant to us — no signature is ever checked — but the field is
      // mandatory.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 }
      ],
      authenticatorSelection: { userVerification: 'required', residentKey: 'preferred' },
      extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs
    }
  })) as PublicKeyCredential | null;

  if (!created) return null;

  const credentialId = toBase64Url(new Uint8Array(created.rawId));

  let prf = prfOutput(created);
  if (!prf) {
    const asserted = (await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        rpId: rpId(),
        allowCredentials: [{ type: 'public-key', id: created.rawId }],
        userVerification: 'required',
        extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs
      }
    })) as PublicKeyCredential | null;
    prf = prfOutput(asserted);
  }

  // The authenticator will not do PRF. Nothing has been stored, so the account
  // is exactly as it was and the caller can say so plainly.
  if (!prf) return null;

  // The one place an extractable master key exists, and only for these two
  // lines. See the module comment.
  const vault = await unlockForRewrap(secret, blobs);
  const masterRaw = new Uint8Array(
    await crypto.subtle.exportKey('raw', vault.masterKey)
  ) as Bytes;

  const wrapped = await aesEncrypt(await wrappingKeyFrom(prf), masterRaw, WRAP_AAD_MASTER);
  masterRaw.fill(0);
  prf.fill(0);

  return { credentialId, prfSalt: toBase64(salt), masterKeyWrapped: wrapped };
}

/**
 * Open the vault with an enrolled passkey.
 *
 * Every credential the account has enrolled is offered at once, so the browser
 * picks whichever the person actually has to hand. Whichever answers, its own
 * salt and its own wrapped copy are the ones used — which is why the salt is
 * per credential rather than per account.
 */
export async function unlockWithPasskey(passkeys: StoredPasskey[]): Promise<Bytes | null> {
  if (!passkeys.length) return null;

  // One salt has to be named up front, before we know which credential will
  // answer. They are all evaluated against the same request, so the first is as
  // good as any — and the response tells us which credential replied, so the
  // *right* salt is used for the real evaluation below when they differ.
  const byId = new Map(passkeys.map((p) => [p.credentialId, p]));

  const assert = async (salt: Bytes) =>
    (await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        rpId: rpId(),
        allowCredentials: passkeys.map((p) => ({
          type: 'public-key' as const,
          id: fromBase64Url(p.credentialId)
        })),
        userVerification: 'required',
        extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs
      }
    })) as PublicKeyCredential | null;

  let credential = await assert(fromBase64(passkeys[0]!.prfSalt));
  if (!credential) return null;

  let used = byId.get(toBase64Url(new Uint8Array(credential.rawId)));
  if (!used) return null;

  // A different credential answered than the salt we guessed. Ask again with
  // its own salt — one extra prompt, only when several are enrolled and the
  // user picked a later one.
  if (used.credentialId !== passkeys[0]!.credentialId) {
    credential = await assert(fromBase64(used.prfSalt));
    if (!credential) return null;
    used = byId.get(toBase64Url(new Uint8Array(credential.rawId)));
    if (!used) return null;
  }

  const prf = prfOutput(credential);
  if (!prf) return null;

  const masterRaw = await aesDecrypt(
    await wrappingKeyFrom(prf),
    used.masterKeyWrapped,
    WRAP_AAD_MASTER
  );
  prf.fill(0);

  return masterRaw;
}

/** Import the bytes a passkey unwrapped as a non-extractable master key —
 *  identical to what the password path produces, so everything downstream is
 *  the same. */
export async function masterKeyFrom(masterRaw: Bytes): Promise<CryptoKey> {
  const key = await importAesKey(masterRaw, false);
  masterRaw.fill(0);
  return key;
}
